import { useState, useEffect, useCallback } from 'react';
import {
  getAdminSeries,
  createAdminSeries,
  getAdminSeriesTemplates,
} from '../../api/admin';
import AdminSeriesDetail from './AdminSeriesDetail';
import s from './AdminPages.module.css';

export default function AdminLibrary() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [episodeCounts, setEpisodeCounts] = useState({});

  const loadSeries = useCallback(() => {
    setLoading(true);
    getAdminSeries()
      .then((data) => {
        const list = data?.series || [];
        setSeries(list);
        Promise.all(
          list.map((item) =>
            getAdminSeriesTemplates(item.id)
              .then((d) => ({ id: item.id, count: d?.templates?.length ?? 0 }))
              .catch(() => ({ id: item.id, count: '?' }))
          )
        ).then((counts) => {
          const map = {};
          counts.forEach(({ id, count }) => { map[id] = count; });
          setEpisodeCounts(map);
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSeries(); }, [loadSeries]);

  function handleSelectSeries(id, name) {
    setSelectedSeriesId(id);
    setSelectedSeriesName(name);
  }

  function handleBack() {
    setSelectedSeriesId(null);
    setSelectedSeriesName('');
    loadSeries();
  }

  if (selectedSeriesId) {
    return (
      <AdminSeriesDetail
        seriesId={selectedSeriesId}
        seriesName={selectedSeriesName}
        onBack={handleBack}
      />
    );
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <h1 className={s.title}>Library</h1>
        <button className={s.primaryBtn} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Series'}
        </button>
      </div>

      {showCreate && (
        <CreateSeriesForm
          onCreated={(newSeries) => {
            setShowCreate(false);
            setSeries((prev) => [...prev, newSeries]);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && <p className={s.errorText}>Error: {error}</p>}

      {loading ? (
        <p className={s.loadingText}>Loading series...</p>
      ) : series.length === 0 ? (
        <div className={s.emptyHint}>
          No series yet. Create one to get started.
        </div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>Name</th>
                <th className={s.th}>Episodes</th>
                <th className={s.th}>Free</th>
                <th className={s.th}>Status</th>
                <th className={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {series.map((item) => (
                <tr key={item.id}>
                  <td className={s.td}>
                    <span className={s.seriesNameCell}>{item.name}</span>
                    {item.description && (
                      <span className={s.seriesDescCell}>{item.description}</span>
                    )}
                  </td>
                  <td className={s.td}>{episodeCounts[item.id] ?? '…'}</td>
                  <td className={s.td}>{item.free_episode_count}</td>
                  <td className={s.td}>
                    <span className={item.is_active ? s.badgeActive : s.badgeDraft}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className={s.td}>
                    <button
                      className={s.linkBtn}
                      onClick={() => handleSelectSeries(item.id, item.name)}
                    >
                      Manage →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateSeriesForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [freeCount, setFreeCount] = useState(3);
  const [isFeatured, setIsFeatured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const result = await createAdminSeries({
        name: name.trim(),
        description: description.trim() || undefined,
        free_episode_count: parseInt(freeCount, 10),
        is_featured: isFeatured,
      });
      onCreated(result.series);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={s.inlineForm} onSubmit={handleSubmit}>
      <h3 className={s.formTitle}>New Series</h3>
      {error && <p className={s.errorText}>{error}</p>}
      <div className={s.formRow}>
        <label className={s.label}>
          Series name *
          <input
            className={s.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Adventures of Qi — Season 1"
            required
          />
        </label>
        <label className={s.label}>
          Free episodes
          <input
            className={s.input}
            type="number"
            min={0}
            max={99}
            value={freeCount}
            onChange={(e) => setFreeCount(e.target.value)}
          />
        </label>
      </div>
      <label className={s.label}>
        Description
        <input
          className={s.input}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A short description shown in the library"
        />
      </label>
      <label>
        <input
          type="checkbox"
          checked={isFeatured}
          onChange={(e) => setIsFeatured(e.target.checked)}
        />
        {' '}Featured series (sorts to top, shows ★ in dropdown)
      </label>
      <div className={s.formActions}>
        <button type="submit" className={s.primaryBtn} disabled={saving}>
          {saving ? 'Creating…' : 'Create Series'}
        </button>
        <button type="button" className={s.secondaryBtn} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
